import React, { Fragment, useState, useEffect } from "react";
import { closeModal, openModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faChevronRight,
  faChevronDown,
  faFileExcel,
} from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import SubmitButton from "../Forms/SubmitButton";
import { getBooking } from "../../store/booking/bookingActions";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ModalATLASReport(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { current_booking } = useSelector((state) => state.booking);
  const staff = useSelector((state) => state.staff?.staff || []);
  const [queueData, setQueueData] = useState([]);
  const [bookingYear, setBookingYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [propertyDriver, setPropertyDriver] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [expandedProperties, setExpandedProperties] = useState({});
  const [unitTypeBreakdowns, setUnitTypeBreakdowns] = useState({});
  const [folderBreakdown, setFolderBreakdown] = useState(null);
  const [loadingFolderBreakdown, setLoadingFolderBreakdown] = useState(false);
  const [showExcludedProperties, setShowExcludedProperties] = useState(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  const folderBreakdownRef = React.useRef(null);

  useEffect(() => {
    fetchAtlasReport();
  }, [includeClosed]);

  // Scroll to folder breakdown when it's loaded
  useEffect(() => {
    if (folderBreakdown && folderBreakdownRef.current) {
      setTimeout(() => {
        folderBreakdownRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    }
  }, [folderBreakdown]);

  const togglePropertyExpansion = async (propertyId) => {
    const isExpanded = expandedProperties[propertyId];

    if (isExpanded) {
      // Collapse
      setExpandedProperties((prev) => ({ ...prev, [propertyId]: false }));
    } else {
      // Expand and fetch unit type breakdown if not already loaded
      setExpandedProperties((prev) => ({ ...prev, [propertyId]: true }));

      if (!unitTypeBreakdowns[propertyId]) {
        await fetchUnitTypeBreakdown(propertyId);
      }
    }
  };

  const fetchUnitTypeBreakdown = async (propertyId) => {
    try {
      const isQL = process.env.REACT_APP_COLOUR === "QL";
      const endpoint = isQL ? "ql-atlas-report" : "atlas-report";
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          booking_id: props.booking_id,
          property_id: propertyId,
          include_closed: includeClosed,
        },
      );

      // Get unit types for this property
      const unitTypesResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}unit_types/${propertyId}`,
      );

      // Fetch data for each unit type
      const unitTypeData = await Promise.all(
        unitTypesResponse.data.map(async (unitType) => {
          const utResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}${endpoint}`,
            {
              booking_id: props.booking_id,
              property_id: propertyId,
              unit_type_id: unitType.value,
              include_closed: includeClosed,
            },
          );
          return {
            unitTypeName: unitType.label,
            data: utResponse.data.queueData[0] || {},
          };
        }),
      );

      setUnitTypeBreakdowns((prev) => ({
        ...prev,
        [propertyId]: unitTypeData,
      }));
    } catch (error) {
      console.error("Error fetching unit type breakdown:", error);
    }
  };

  const fetchAtlasReport = async () => {
    setLoading(true);
    try {
      const isQL = process.env.REACT_APP_COLOUR === "QL";
      const endpoint = isQL ? "ql-atlas-report" : "atlas-report";
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          booking_id: props.booking_id,
          include_closed: includeClosed,
        },
      );

      setQueueData(response.data.queueData);
      setBookingYear(response.data.bookingYear);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching ATLAS report:", error);
      setLoading(false);
    }
  };

  // Calculate totals (excluding properties with exclude_from_atlas_total flag)
  const calculateTotals = () => {
    if (!queueData || queueData.length === 0) return null;

    const isQL = process.env.REACT_APP_COLOUR === "QL";

    // Filter out excluded properties for the totals calculation
    const includedProperties = queueData.filter(
      (queue) => !queue.exclude_from_atlas_total,
    );

    if (isQL) {
      // QL columns: no_units, leads, viewings, documents, vetting, approved, lease_signed, move_in_booked, onboarding
      const totals = includedProperties.reduce(
        (acc, queue) => {
          return {
            no_units: acc.no_units + (queue.no_units || 0),
            leads: acc.leads + (queue.leads || 0),
            viewings: acc.viewings + (queue.viewings || 0),
            documents: acc.documents + (queue.documents || 0),
            vetting: acc.vetting + (queue.vetting || 0),
            approved: acc.approved + (queue.approved || 0),
            lease_signed: acc.lease_signed + (queue.lease_signed || 0),
            move_in_booked: acc.move_in_booked + (queue.move_in_booked || 0),
            onboarding: acc.onboarding + (queue.onboarding || 0),
            total_bookings: acc.total_bookings + (queue.total_bookings || 0),
            from_lease_signed: acc.from_lease_signed + (queue.from_lease_signed || 0),
            from_move_in_booked: acc.from_move_in_booked + (queue.from_move_in_booked || 0),
          };
        },
        {
          no_units: 0,
          leads: 0,
          viewings: 0,
          documents: 0,
          vetting: 0,
          approved: 0,
          lease_signed: 0,
          move_in_booked: 0,
          onboarding: 0,
          total_bookings: 0,
          from_lease_signed: 0,
          from_move_in_booked: 0,
        },
      );

      totals.percent =
        totals.no_units > 0 ? (totals.total_bookings / totals.no_units) * 100 : 0;
      totals.percent_from_lease_signed =
        totals.no_units > 0
          ? (totals.from_lease_signed / totals.no_units) * 100
          : 0;
      totals.percent_from_move_in_booked =
        totals.no_units > 0
          ? (totals.from_move_in_booked / totals.no_units) * 100
          : 0;

      return totals;
    }

    // UC columns
    const totals = includedProperties.reduce(
      (acc, queue) => {
        return {
          no_beds: acc.no_beds + (queue.no_beds || 0),
          uj_students_total:
            acc.uj_students_total + (queue.uj_students_total || 0),
          bs_kids: acc.bs_kids + (queue.bs_kids || 0),
          bs_1st: acc.bs_1st + (queue.bs_1st || 0),
          nsfas_uj_1st_bs: acc.nsfas_uj_1st_bs + (queue.nsfas_uj_1st_bs || 0),
          bs_2nd_plus: acc.bs_2nd_plus + (queue.bs_2nd_plus || 0),
          vetting: acc.vetting + (queue.vetting || 0),
          lease_sent: acc.lease_sent + (queue.lease_sent || 0),
          lease_signed: acc.lease_signed + (queue.lease_signed || 0),
          renewal_lease_sent:
            acc.renewal_lease_sent + (queue.renewal_lease_sent || 0),
          renewal_lease_signed:
            acc.renewal_lease_signed + (queue.renewal_lease_signed || 0),
          move_in_booked: acc.move_in_booked + (queue.move_in_booked || 0),
          checking_in: acc.checking_in + (queue.checking_in || 0),
          moved_in: acc.moved_in + (queue.moved_in || 0),
          moved_in_blocked: acc.moved_in_blocked + (queue.moved_in_blocked || 0),
          mda_onboarding: acc.mda_onboarding + (queue.mda_onboarding || 0),
          total_bookings: acc.total_bookings + (queue.total_bookings || 0),
          from_lease_sent: acc.from_lease_sent + (queue.from_lease_sent || 0),
          from_lease_signed:
            acc.from_lease_signed + (queue.from_lease_signed || 0),
          from_move_in_booked:
            acc.from_move_in_booked + (queue.from_move_in_booked || 0),
          from_moved_in: acc.from_moved_in + (queue.from_moved_in || 0),
          total_uj_apps: acc.total_uj_apps + (queue.total_uj_apps || 0),
          uj_tracker: acc.uj_tracker + (queue.uj_tracker || 0),
          uj_tracker_inc_bursary:
            acc.uj_tracker_inc_bursary + (queue.uj_tracker_inc_bursary || 0),
        };
      },
      {
        no_beds: 0,
        uj_students_total: 0,
        bs_kids: 0,
        bs_1st: 0,
        nsfas_uj_1st_bs: 0,
        bs_2nd_plus: 0,
        vetting: 0,
        lease_sent: 0,
        lease_signed: 0,
        renewal_lease_sent: 0,
        renewal_lease_signed: 0,
        move_in_booked: 0,
        checking_in: 0,
        moved_in: 0,
        moved_in_blocked: 0,
        mda_onboarding: 0,
        total_bookings: 0,
        from_lease_sent: 0,
        from_lease_signed: 0,
        from_move_in_booked: 0,
        from_moved_in: 0,
        total_uj_apps: 0,
        uj_tracker: 0,
        uj_tracker_inc_bursary: 0,
      },
    );

    totals.percent =
      totals.no_beds > 0 ? (totals.total_bookings / totals.no_beds) * 100 : 0;
    totals.percent_from_lease_sent =
      totals.no_beds > 0 ? (totals.from_lease_sent / totals.no_beds) * 100 : 0;
    totals.percent_from_lease_signed =
      totals.no_beds > 0
        ? (totals.from_lease_signed / totals.no_beds) * 100
        : 0;
    totals.percent_from_move_in_booked =
      totals.no_beds > 0
        ? (totals.from_move_in_booked / totals.no_beds) * 100
        : 0;
    totals.percent_from_moved_in =
      totals.no_beds > 0 ? (totals.from_moved_in / totals.no_beds) * 100 : 0;
    totals.total_uj_apps_percent =
      totals.uj_students_total > 0
        ? (totals.total_uj_apps / totals.uj_students_total) * 100
        : 0;
    totals.uj_tracker_percent =
      totals.uj_students_total > 0
        ? (totals.uj_tracker / totals.uj_students_total) * 100
        : 0;
    totals.uj_tracker_inc_bursary_percent =
      totals.uj_students_total > 0
        ? (totals.uj_tracker_inc_bursary / totals.uj_students_total) * 100
        : 0;

    return totals;
  };

  const totals = calculateTotals();

  // Split properties into included and excluded
  const includedProperties =
    queueData?.filter((queue) => !queue.exclude_from_atlas_total) || [];
  const excludedProperties =
    queueData?.filter((queue) => queue.exclude_from_atlas_total) || [];

  // Calculate totals for excluded properties
  const calculateExcludedTotals = () => {
    if (!excludedProperties || excludedProperties.length === 0) return null;

    const totals = excludedProperties.reduce(
      (acc, queue) => {
        return {
          no_beds: acc.no_beds + (queue.no_beds || 0),
          uj_students_total:
            acc.uj_students_total + (queue.uj_students_total || 0),
          bs_kids: acc.bs_kids + (queue.bs_kids || 0),
          bs_1st: acc.bs_1st + (queue.bs_1st || 0),
          nsfas_uj_1st_bs: acc.nsfas_uj_1st_bs + (queue.nsfas_uj_1st_bs || 0),
          bs_2nd_plus: acc.bs_2nd_plus + (queue.bs_2nd_plus || 0),
          vetting: acc.vetting + (queue.vetting || 0),
          lease_sent: acc.lease_sent + (queue.lease_sent || 0),
          lease_signed: acc.lease_signed + (queue.lease_signed || 0),
          renewal_lease_sent:
            acc.renewal_lease_sent + (queue.renewal_lease_sent || 0),
          renewal_lease_signed:
            acc.renewal_lease_signed + (queue.renewal_lease_signed || 0),
          move_in_booked: acc.move_in_booked + (queue.move_in_booked || 0),
          checking_in: acc.checking_in + (queue.checking_in || 0),
          moved_in: acc.moved_in + (queue.moved_in || 0),
          moved_in_blocked: acc.moved_in_blocked + (queue.moved_in_blocked || 0),
          mda_onboarding: acc.mda_onboarding + (queue.mda_onboarding || 0),
          total_bookings: acc.total_bookings + (queue.total_bookings || 0),
          from_lease_sent: acc.from_lease_sent + (queue.from_lease_sent || 0),
          from_lease_signed:
            acc.from_lease_signed + (queue.from_lease_signed || 0),
          from_move_in_booked:
            acc.from_move_in_booked + (queue.from_move_in_booked || 0),
          from_moved_in: acc.from_moved_in + (queue.from_moved_in || 0),
          total_uj_apps: acc.total_uj_apps + (queue.total_uj_apps || 0),
          uj_tracker: acc.uj_tracker + (queue.uj_tracker || 0),
          uj_tracker_inc_bursary:
            acc.uj_tracker_inc_bursary + (queue.uj_tracker_inc_bursary || 0),
        };
      },
      {
        no_beds: 0,
        uj_students_total: 0,
        bs_kids: 0,
        bs_1st: 0,
        nsfas_uj_1st_bs: 0,
        bs_2nd_plus: 0,
        vetting: 0,
        lease_sent: 0,
        lease_signed: 0,
        renewal_lease_sent: 0,
        renewal_lease_signed: 0,
        move_in_booked: 0,
        checking_in: 0,
        moved_in: 0,
        moved_in_blocked: 0,
        mda_onboarding: 0,
        total_bookings: 0,
        from_lease_sent: 0,
        from_lease_signed: 0,
        from_move_in_booked: 0,
        from_moved_in: 0,
        total_uj_apps: 0,
        uj_tracker: 0,
        uj_tracker_inc_bursary: 0,
      },
    );

    totals.percent =
      totals.no_beds > 0 ? (totals.total_bookings / totals.no_beds) * 100 : 0;
    totals.percent_from_lease_sent =
      totals.no_beds > 0 ? (totals.from_lease_sent / totals.no_beds) * 100 : 0;
    totals.percent_from_lease_signed =
      totals.no_beds > 0
        ? (totals.from_lease_signed / totals.no_beds) * 100
        : 0;
    totals.percent_from_move_in_booked =
      totals.no_beds > 0
        ? (totals.from_move_in_booked / totals.no_beds) * 100
        : 0;
    totals.percent_from_moved_in =
      totals.no_beds > 0 ? (totals.from_moved_in / totals.no_beds) * 100 : 0;
    totals.total_uj_apps_percent =
      totals.uj_students_total > 0
        ? (totals.total_uj_apps / totals.uj_students_total) * 100
        : 0;
    totals.uj_tracker_percent =
      totals.uj_students_total > 0
        ? (totals.uj_tracker / totals.uj_students_total) * 100
        : 0;
    totals.uj_tracker_inc_bursary_percent =
      totals.uj_students_total > 0
        ? (totals.uj_tracker_inc_bursary / totals.uj_students_total) * 100
        : 0;

    return totals;
  };

  const excludedTotals = calculateExcludedTotals();

  // Handle click on a number to show breakdown
  const handleBreakdownClick = async (propertyId, location, propertyName) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}atlas-report-breakdown`,
        {
          property_id: propertyId,
          location: location,
          year_id: current_booking?.year_id || 3,
          include_closed: includeClosed,
        },
      );
      // Add property_id and location to breakdown data for bulk messaging
      setBreakdown({
        ...response.data,
        property_id: propertyId,
        location: location,
      });
    } catch (error) {
      console.error("Error fetching breakdown:", error);
      toast.error("Failed to load breakdown");
    }
  };

  // Handle click on property name to show driver view
  const handlePropertyClick = async (propertyId, propertyName) => {
    setLoadingDriver(true);
    setPropertyDriver(null);
    setBreakdown(null); // Clear breakdown when showing driver
    setFolderBreakdown(null); // Clear folder breakdown when showing driver

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}atlas-property-driver`,
        {
          property_id: propertyId,
          year_id: current_booking?.year_id || 3,
        },
      );
      setPropertyDriver(response.data);
    } catch (error) {
      console.error("Error fetching property driver:", error);
      toast.error("Failed to load property driver");
    } finally {
      setLoadingDriver(false);
    }
  };

  // Handle click on folder column header to show funding type breakdown
  const handleFolderBreakdownClick = async (location) => {
    setLoadingFolderBreakdown(true);
    setFolderBreakdown(null);
    setBreakdown(null); // Clear property breakdown
    setPropertyDriver(null); // Clear property driver

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}atlas-folder-breakdown`,
        {
          location: location,
          year_id: current_booking?.year_id || 3,
        },
      );
      setFolderBreakdown(response.data);
    } catch (error) {
      console.error("Error fetching folder breakdown:", error);
      toast.error("Failed to load folder breakdown");
    } finally {
      setLoadingFolderBreakdown(false);
    }
  };

  // Map location display names to database location names
  const getLocationName = (columnName) => {
    const locationMap = {
      bs_kids: "BS Cash",
      bs_1st: "BS 1st",
      nsfas_uj_1st_bs: "NSFAS UJ 1st BS",
      bs_2nd_plus: "BS 2nd +",
      vetting: "Vetting",
      lease_sent: "Lease Sent",
      lease_signed: "Lease Signed",
      renewal_lease_sent: "Renewal Lease Sent",
      renewal_lease_signed: "Renewal Lease Signed",
      move_in_booked: "Move In Booked",
      checking_in: "Checking In",
      moved_in: "Moved In",
      mda_onboarding: "MDA Onboarding",
    };
    return locationMap[columnName] || columnName;
  };

  // Handle bulk message sending from breakdown
  const handleBulkMessage = async (studentTypeId = null) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}atlas-bookings-for-bulk`,
        {
          property_id: breakdown.property_id,
          location: breakdown.location,
          year_id: current_booking?.year_id || 3,
          student_type_id: studentTypeId,
        },
      );

      // Open bulk message modal with pre-filtered bookings
      dispatch(
        openModal({
          modal_type: "ModalBulkMessages",
          modal_props: {
            bookings: response.data.bookings,
            allBookings: response.data.bookings,
            preFiltered: true,
            filterContext: response.data.filter_context,
            current_staff: current_booking?.assigned_to_staff,
            staff: staff || [],
            lookups: props.lookups,
            onBackToAtlas: () => {
              dispatch(
                openModal({
                  modal_type: "ModalATLASReport",
                  modal_props: props,
                }),
              );
            },
          },
        }),
      );
    } catch (error) {
      console.error("Error fetching bookings for bulk message:", error);
      toast.error("Failed to load bookings for bulk messaging");
    }
  };

  // Handle bulk message sending from Building Driver matrix
  const handleDriverBulkMessage = async (
    studyYear = null,
    fundingType = null,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}atlas-driver-bookings-for-bulk`,
        {
          property_id: propertyDriver.property_id,
          year_id: current_booking?.year_id || 3,
          study_year: studyYear,
          funding_type: fundingType,
        },
      );

      // Open bulk message modal with pre-filtered bookings
      dispatch(
        openModal({
          modal_type: "ModalBulkMessages",
          modal_props: {
            bookings: response.data.bookings,
            allBookings: response.data.bookings,
            preFiltered: true,
            filterContext: {
              ...response.data.filter_context,
              source: "Building Driver",
            },
            current_staff: current_booking?.assigned_to_staff,
            staff: staff || [],
            lookups: props.lookups,
            onBackToAtlas: () => {
              dispatch(
                openModal({
                  modal_type: "ModalATLASReport",
                  modal_props: props,
                }),
              );
            },
          },
        }),
      );
    } catch (error) {
      console.error("Error fetching driver bookings for bulk message:", error);
      toast.error("Failed to load bookings for bulk messaging");
    }
  };

  // Download to Excel function
  const downloadToExcel = async () => {
    if (!queueData || queueData.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const endpoint = process.env.REACT_APP_COLOUR === "QL" 
        ? "ql-atlas-report-export" 
        : "atlas-report-export";
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        {
          booking_id: props.booking_id,
        },
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filePrefix = process.env.REACT_APP_COLOUR === "QL" ? "QL_ATLAS_Report" : "ATLAS_Report";
      link.setAttribute(
        "download",
        `${filePrefix}_${bookingYear || "Current"}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Excel file downloaded successfully");
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      toast.error("Failed to download Excel file");
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4 flex gap-6">
                  <button
                    type="button"
                    className="bg-white rounded-md text-green-600 hover:text-green-700 focus:outline-none focus:ring-0 mr-6"
                    onClick={downloadToExcel}
                    title="Download to Excel"
                  >
                    <span className="sr-only">Download to Excel</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faFileExcel}
                    />
                  </button>
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div className="pr-8 w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900"
                    ></Dialog.Title>

                    <div>
                      <div className="flex items-center justify-center gap-3">
                        <h1 className="text-center font-bold">
                          ATLAS Report {bookingYear && `(Year: ${bookingYear})`}
                        </h1>
                        <button
                          type="button"
                          onClick={() => { setUnitTypeBreakdowns({}); setExpandedProperties({}); fetchAtlasReport(); }}
                          disabled={loading}
                          className="px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? "Refreshing..." : "Refresh"}
                        </button>
                      </div>

                      <div className="flex justify-center items-center gap-2 my-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={includeClosed}
                            onChange={(e) => setIncludeClosed(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            Include Closed Bookings
                          </span>
                        </label>
                      </div>

                      {loading === true ? (
                        <p className="text-center">Loading report...</p>
                      ) : (
                        <>
                          {!breakdown && !propertyDriver && (
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead>
                              <tr>
                                <th
                                  scope="col"
                                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                                ></th>
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  {process.env.REACT_APP_COLOUR === "QL" ? "No Units" : "No Beds"}
                                </th>
                                {process.env.REACT_APP_COLOUR === "QL" ? (
                                  <>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Leads
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Viewings
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Documents
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Vetting
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Approved
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Lease Signed
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Move In Booked
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Onboarding
                                    </th>
                                  </>
                                ) : (
                                  <>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      BS Kids
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      BS 1st
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      NSFAS UJ 1st BS
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      BS 2nd +
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Vetting
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Lease Sent
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Lease Signed
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Renewal Lease Sent
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Renewal Lease Signed
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Move In Booked
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Checking In
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      Moved In
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                    >
                                      MDA Onboarding
                                    </th>
                                  </>
                                )}
                                <th
                                  scope="col"
                                  className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                >
                                  Total
                                  <br />
                                  Percentage
                                </th>
                                {process.env.REACT_APP_COLOUR !== "QL" && (
                                  <>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                    >
                                      Total UJ Apps
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                    >
                                      UJ Accredited Beds
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                    >
                                      UJ Tracker
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                    >
                                      UJ Tracker inc Bursary
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-green-50"
                                    >
                                      <button
                                        onClick={() =>
                                          handleFolderBreakdownClick("Lease Sent")
                                        }
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                      >
                                        Total %
                                        <br />
                                        (From Lease Sent)
                                      </button>
                                    </th>
                                  </>
                                )}
                                {process.env.REACT_APP_COLOUR === "QL" ? (
                                  <>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-blue-50"
                                    >
                                      Total %
                                      <br />
                                      (From Lease Signed)
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-purple-50"
                                    >
                                      Total %
                                      <br />
                                      (From Move In Booked)
                                    </th>
                                  </>
                                ) : (
                                  <>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-blue-50"
                                    >
                                      <button
                                        onClick={() =>
                                          handleFolderBreakdownClick("Lease Signed")
                                        }
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                      >
                                        Total %
                                        <br />
                                        (From Lease Signed)
                                      </button>
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-purple-50"
                                    >
                                      <button
                                        onClick={() =>
                                          handleFolderBreakdownClick(
                                            "Move In Booked",
                                          )
                                        }
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                      >
                                        Total %
                                        <br />
                                        (From Move In Booked)
                                      </button>
                                    </th>
                                    <th
                                      scope="col"
                                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-orange-50"
                                    >
                                      <button
                                        onClick={() =>
                                          handleFolderBreakdownClick("Moved In")
                                        }
                                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                      >
                                        Total %
                                        <br />
                                        (From Moved In)
                                      </button>
                                    </th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {includedProperties?.map((queue, index) => (
                                <React.Fragment key={index}>
                                  <tr>
                                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 text-left">
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() =>
                                            togglePropertyExpansion(
                                              queue.property_id,
                                            )
                                          }
                                          className="text-gray-600 hover:text-gray-900"
                                        >
                                          <FontAwesomeIcon
                                            icon={
                                              expandedProperties[
                                                queue.property_id
                                              ]
                                                ? faChevronDown
                                                : faChevronRight
                                            }
                                            className="w-4 h-4"
                                          />
                                        </button>
                                        {process.env.REACT_APP_COLOUR === "QL" ? (
                                          <span className="font-semibold text-gray-900 text-left">
                                            {queue.property_name}
                                          </span>
                                        ) : (
                                          <button
                                            onClick={() =>
                                              handlePropertyClick(
                                                queue.property_id,
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-semibold text-left"
                                          >
                                            {queue.property_name}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500">
                                      {process.env.REACT_APP_COLOUR === "QL" ? queue.no_units : queue.no_beds}
                                    </td>
                                    {process.env.REACT_APP_COLOUR === "QL" ? (
                                      <>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.leads}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.viewings}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.documents}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.vetting}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.approved}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.lease_signed}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.move_in_booked}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          {queue.onboarding}
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "BS Cash",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.bs_kids}
                                          >
                                            {queue.bs_kids}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "BS 1st",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.bs_1st}
                                          >
                                            {queue.bs_1st}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "NSFAS UJ 1st BS",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.nsfas_uj_1st_bs}
                                          >
                                            {queue.nsfas_uj_1st_bs}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "BS 2nd +",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.bs_2nd_plus}
                                          >
                                            {queue.bs_2nd_plus}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Vetting",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.vetting}
                                          >
                                            {queue.vetting}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Lease Sent",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.lease_sent}
                                          >
                                            {queue.lease_sent}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Lease Signed",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.lease_signed}
                                          >
                                            {queue.lease_signed}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Renewal Lease Sent",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.renewal_lease_sent}
                                          >
                                            {queue.renewal_lease_sent}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Renewal Lease Signed",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.renewal_lease_signed}
                                          >
                                            {queue.renewal_lease_signed}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Move In Booked",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.move_in_booked}
                                          >
                                            {queue.move_in_booked}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Checking In",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.checking_in}
                                          >
                                            {queue.checking_in}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "Moved In",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.moved_in}
                                          >
                                            {queue.moved_in}{queue.moved_in_blocked > 0 && <><br /><span className="text-red-500 text-xs">({queue.moved_in_blocked})</span></>}
                                          </button>
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500">
                                          <button
                                            onClick={() =>
                                              handleBreakdownClick(
                                                queue.property_id,
                                                "MDA Onboarding",
                                                queue.property_name,
                                              )
                                            }
                                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                            disabled={!queue.mda_onboarding}
                                          >
                                            {queue.mda_onboarding}
                                          </button>
                                        </td>
                                      </>
                                    )}
                                    <td className="px-3 py-4 text-sm text-gray-500">
                                      {queue.total_bookings}
                                      <br />
                                      {queue.percent?.toFixed(2)}%
                                    </td>
                                    {process.env.REACT_APP_COLOUR !== "QL" && (
                                      <>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                          {queue.total_uj_apps}
                                          <br />
                                          {queue.total_uj_apps_percent?.toFixed(2)}%
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                          {queue.uj_students_total}
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                          {queue.uj_tracker}
                                          <br />
                                          {queue.uj_tracker_percent?.toFixed(2)}%
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                          {queue.uj_tracker_inc_bursary}
                                          <br />
                                          {queue.uj_tracker_inc_bursary_percent?.toFixed(2)}%
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-green-50">
                                          {queue.from_lease_sent}
                                          <br />
                                          {queue.percent_from_lease_sent?.toFixed(
                                            2,
                                          )}
                                          %
                                        </td>
                                      </>
                                    )}
                                    {process.env.REACT_APP_COLOUR === "QL" ? (
                                      <>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-blue-50">
                                          {queue.from_lease_signed}
                                          <br />
                                          {queue.percent_from_lease_signed?.toFixed(
                                            2,
                                          )}
                                          %
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-purple-50">
                                          {queue.from_move_in_booked}
                                          <br />
                                          {queue.percent_from_move_in_booked?.toFixed(
                                            2,
                                          )}
                                          %
                                        </td>
                                      </>
                                    ) : (
                                      <>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-blue-50">
                                          {queue.from_lease_signed}
                                          <br />
                                          {queue.percent_from_lease_signed?.toFixed(
                                            2,
                                          )}
                                          %
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-purple-50">
                                          {queue.from_move_in_booked}
                                          <br />
                                          {queue.percent_from_move_in_booked?.toFixed(
                                            2,
                                          )}
                                          %
                                        </td>
                                        <td className="px-3 py-4 text-sm text-gray-500 bg-orange-50">
                                          {queue.from_moved_in}
                                          <br />
                                          {queue.percent_from_moved_in?.toFixed(2)}%
                                        </td>
                                      </>
                                    )}
                                  </tr>

                                  {/* Unit Type Breakdown Rows */}
                                  {expandedProperties[queue.property_id] &&
                                    unitTypeBreakdowns[queue.property_id]?.map(
                                      (unitTypeData, utIndex) => (
                                        <tr
                                          key={`${index}-${utIndex}`}
                                          className="bg-gray-50"
                                        >
                                          <td className="py-2 pl-12 pr-3 text-sm text-gray-700 sm:pl-8 text-left italic">
                                            {unitTypeData.unitTypeName}
                                          </td>
                                          <td className="px-3 py-2 text-sm text-gray-600">
                                            {process.env.REACT_APP_COLOUR === "QL" ? (unitTypeData.data.no_units || 0) : (unitTypeData.data.no_beds || 0)}
                                          </td>
                                          {process.env.REACT_APP_COLOUR === "QL" ? (
                                            <>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.leads || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.viewings || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.documents || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.vetting || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.approved || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.lease_signed || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.move_in_booked || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.onboarding || 0}
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.bs_kids || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.bs_1st || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data
                                                  .nsfas_uj_1st_bs || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.bs_2nd_plus || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.vetting || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.lease_sent || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.lease_signed ||
                                                  0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data
                                                  .renewal_lease_sent || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data
                                                  .renewal_lease_signed || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.move_in_booked ||
                                                  0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.checking_in || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.moved_in || 0}{unitTypeData.data.moved_in_blocked > 0 && <><br /><span className="text-red-500 text-xs">({unitTypeData.data.moved_in_blocked})</span></>}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600">
                                                {unitTypeData.data.mda_onboarding ||
                                                  0}
                                              </td>
                                            </>
                                          )}
                                          <td className="px-3 py-2 text-sm text-gray-600">
                                            {unitTypeData.data.total_bookings ||
                                              0}
                                          </td>
                                          {process.env.REACT_APP_COLOUR !== "QL" && (
                                            <>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-yellow-50">
                                                {unitTypeData.data.total_uj_apps ||
                                                  0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-yellow-50">
                                                {unitTypeData.data
                                                  .uj_students_total || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-yellow-50">
                                                {unitTypeData.data.uj_tracker || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-yellow-50">
                                                {unitTypeData.data.uj_tracker_inc_bursary || 0}
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-green-50">
                                                {unitTypeData.data
                                                  .from_lease_sent || 0}
                                                <br />
                                                {unitTypeData.data.percent_from_lease_sent?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                            </>
                                          )}
                                          {process.env.REACT_APP_COLOUR === "QL" ? (
                                            <>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-blue-50">
                                                {unitTypeData.data
                                                  .from_lease_signed || 0}
                                                <br />
                                                {unitTypeData.data.percent_from_lease_signed?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-purple-50">
                                                {unitTypeData.data
                                                  .from_move_in_booked || 0}
                                                <br />
                                                {unitTypeData.data.percent_from_move_in_booked?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-blue-50">
                                                {unitTypeData.data
                                                  .from_lease_signed || 0}
                                                <br />
                                                {unitTypeData.data.percent_from_lease_signed?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-purple-50">
                                                {unitTypeData.data
                                                  .from_move_in_booked || 0}
                                                <br />
                                                {unitTypeData.data.percent_from_move_in_booked?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                              <td className="px-3 py-2 text-sm text-gray-600 bg-orange-50">
                                                {unitTypeData.data.from_moved_in ||
                                                  0}
                                                <br />
                                                {unitTypeData.data.percent_from_moved_in?.toFixed(
                                                  2,
                                                ) || 0}
                                                %
                                              </td>
                                            </>
                                          )}
                                        </tr>
                                      ),
                                    )}
                                </React.Fragment>
                              ))}
                              {totals && (
                                <tr className="bg-gray-100 font-bold border-t-2 border-gray-400">
                                  <td className="py-4 pl-4 pr-3 text-sm font-bold text-gray-900 sm:pl-0 text-left">
                                    TOTALS
                                  </td>
                                  <td className="px-3 py-4 text-sm text-gray-900">
                                    {process.env.REACT_APP_COLOUR === "QL" ? totals.no_units : totals.no_beds}
                                  </td>
                                  {process.env.REACT_APP_COLOUR === "QL" ? (
                                    <>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.leads}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.viewings}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.documents}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.vetting}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.approved}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.lease_signed}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.move_in_booked}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.onboarding}
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.bs_kids}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.bs_1st}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.nsfas_uj_1st_bs}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.bs_2nd_plus}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.vetting}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.lease_sent}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.lease_signed}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.renewal_lease_sent}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.renewal_lease_signed}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.move_in_booked}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.checking_in}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.moved_in}{totals.moved_in_blocked > 0 && <><br /><span className="text-red-500 text-xs">({totals.moved_in_blocked})</span></>}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900">
                                        {totals.mda_onboarding}
                                      </td>
                                    </>
                                  )}
                                  <td className="px-3 py-4 text-sm text-gray-900">
                                    {totals.total_bookings}
                                    <br />
                                    {totals.percent?.toFixed(2)}%
                                  </td>
                                  {process.env.REACT_APP_COLOUR !== "QL" && (
                                    <>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                        {totals.total_uj_apps}
                                        <br />
                                        {totals.total_uj_apps_percent?.toFixed(2)}%
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                        {totals.uj_students_total}
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                        {totals.uj_tracker}
                                        <br />
                                        {totals.uj_tracker_percent?.toFixed(2)}%
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                        {totals.uj_tracker_inc_bursary}
                                        <br />
                                        {totals.uj_tracker_inc_bursary_percent?.toFixed(2)}%
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-green-100">
                                        {totals.from_lease_sent}
                                        <br />
                                        {totals.percent_from_lease_sent?.toFixed(2)}
                                        %
                                      </td>
                                    </>
                                  )}
                                  {process.env.REACT_APP_COLOUR === "QL" ? (
                                    <>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-blue-100">
                                        {totals.from_lease_signed}
                                        <br />
                                        {totals.percent_from_lease_signed?.toFixed(
                                          2,
                                        )}
                                        %
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-purple-100">
                                        {totals.from_move_in_booked}
                                        <br />
                                        {totals.percent_from_move_in_booked?.toFixed(
                                          2,
                                        )}
                                        %
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-blue-100">
                                        {totals.from_lease_signed}
                                        <br />
                                        {totals.percent_from_lease_signed?.toFixed(
                                          2,
                                        )}
                                        %
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-purple-100">
                                        {totals.from_move_in_booked}
                                        <br />
                                        {totals.percent_from_move_in_booked?.toFixed(
                                          2,
                                        )}
                                        %
                                      </td>
                                      <td className="px-3 py-4 text-sm text-gray-900 bg-orange-100">
                                        {totals.from_moved_in}
                                        <br />
                                        {totals.percent_from_moved_in?.toFixed(2)}%
                                      </td>
                                    </>
                                  )}
                                </tr>
                              )}
                            </tbody>
                          </table>
                          )}

                          {/* Excluded Properties Accordion */}
                          {!breakdown && !propertyDriver && excludedProperties &&
                            excludedProperties.length > 0 && (
                              <div className="mt-8">
                                <button
                                  onClick={() =>
                                    setShowExcludedProperties(
                                      !showExcludedProperties,
                                    )
                                  }
                                  className="flex items-center gap-2 text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors mb-4"
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      showExcludedProperties
                                        ? faChevronDown
                                        : faChevronRight
                                    }
                                    className="w-4 h-4"
                                  />
                                  Excluded from Main Totals (
                                  {excludedProperties.length}{" "}
                                  {excludedProperties.length === 1
                                    ? "property"
                                    : "properties"}
                                  )
                                </button>
                                {showExcludedProperties && (
                                  <table className="min-w-full divide-y divide-gray-300">
                                    <thead>
                                      <tr>
                                        <th
                                          scope="col"
                                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                                        ></th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          No Beds
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          BS Kids
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          BS 1st
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          NSFAS UJ 1st BS
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          BS 2nd +
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Vetting
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Lease Sent
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Lease Signed
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Renewal Lease Sent
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Renewal Lease Signed
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Move In Booked
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Checking In
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Moved In
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          MDA Onboarding
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                                        >
                                          Total
                                          <br />
                                          Percentage
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                        >
                                          Total UJ Apps
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                        >
                                          UJ Accredited Beds
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-yellow-50"
                                        >
                                          UJ Tracker
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-green-50"
                                        >
                                          Total %<br />
                                          (From Lease Sent)
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-blue-50"
                                        >
                                          Total %<br />
                                          (From Lease Signed)
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-purple-50"
                                        >
                                          Total %<br />
                                          (From Move In Booked)
                                        </th>
                                        <th
                                          scope="col"
                                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 bg-orange-50"
                                        >
                                          Total %<br />
                                          (From Moved In)
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {excludedProperties.map(
                                        (queue, index) => (
                                          <tr key={index}>
                                            <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 text-left">
                                              <button
                                                onClick={() =>
                                                  handlePropertyClick(
                                                    queue.property_id,
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-semibold text-left"
                                              >
                                                {queue.property_name}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              {queue.no_beds}
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "BS Cash",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.bs_kids}
                                              >
                                                {queue.bs_kids}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "BS 1st",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.bs_1st}
                                              >
                                                {queue.bs_1st}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "NSFAS UJ 1st BS",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={
                                                  !queue.nsfas_uj_1st_bs
                                                }
                                              >
                                                {queue.nsfas_uj_1st_bs}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "BS 2nd +",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.bs_2nd_plus}
                                              >
                                                {queue.bs_2nd_plus}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Vetting",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.vetting}
                                              >
                                                {queue.vetting}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Lease Sent",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.lease_sent}
                                              >
                                                {queue.lease_sent}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Lease Signed",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.lease_signed}
                                              >
                                                {queue.lease_signed}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Renewal Lease Sent",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={
                                                  !queue.renewal_lease_sent
                                                }
                                              >
                                                {queue.renewal_lease_sent}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Renewal Lease Signed",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={
                                                  !queue.renewal_lease_signed
                                                }
                                              >
                                                {queue.renewal_lease_signed}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Move In Booked",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.move_in_booked}
                                              >
                                                {queue.move_in_booked}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Checking In",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.checking_in}
                                              >
                                                {queue.checking_in}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "Moved In",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.moved_in}
                                              >
                                                {queue.moved_in}{queue.moved_in_blocked > 0 && <><br /><span className="text-red-500 text-xs">({queue.moved_in_blocked})</span></>}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              <button
                                                onClick={() =>
                                                  handleBreakdownClick(
                                                    queue.property_id,
                                                    "MDA Onboarding",
                                                    queue.property_name,
                                                  )
                                                }
                                                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer disabled:text-gray-500 disabled:cursor-default disabled:no-underline"
                                                disabled={!queue.mda_onboarding}
                                              >
                                                {queue.mda_onboarding}
                                              </button>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                              {queue.total_bookings}
                                              <br />
                                              {queue.percent?.toFixed(2)}%
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                              {queue.total_uj_apps}
                                              <br />
                                              {queue.total_uj_apps_percent?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                              {queue.uj_students_total}
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                              {queue.uj_tracker}
                                              <br />
                                              {queue.uj_tracker_percent?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-yellow-50">
                                              {queue.uj_tracker_inc_bursary}
                                              <br />
                                              {queue.uj_tracker_inc_bursary_percent?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-green-50">
                                              {queue.from_lease_sent}
                                              <br />
                                              {queue.percent_from_lease_sent?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-blue-50">
                                              {queue.from_lease_signed}
                                              <br />
                                              {queue.percent_from_lease_signed?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-purple-50">
                                              {queue.from_move_in_booked}
                                              <br />
                                              {queue.percent_from_move_in_booked?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500 bg-orange-50">
                                              {queue.from_moved_in}
                                              <br />
                                              {queue.percent_from_moved_in?.toFixed(
                                                2,
                                              )}
                                              %
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                      {excludedTotals && (
                                        <tr className="bg-gray-100 font-bold border-t-2 border-gray-400">
                                          <td className="py-4 pl-4 pr-3 text-sm font-bold text-gray-900 sm:pl-0 text-left">
                                            TOTALS
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.no_beds}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.bs_kids}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.bs_1st}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.nsfas_uj_1st_bs}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.bs_2nd_plus}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.vetting}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.lease_sent}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.lease_signed}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.renewal_lease_sent}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {
                                              excludedTotals.renewal_lease_signed
                                            }
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.move_in_booked}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.checking_in}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.moved_in}{excludedTotals.moved_in_blocked > 0 && <><br /><span className="text-red-500 text-xs">({excludedTotals.moved_in_blocked})</span></>}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.mda_onboarding}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900">
                                            {excludedTotals.total_bookings}
                                            <br />
                                            {excludedTotals.percent?.toFixed(2)}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                            {excludedTotals.uj_students_total}
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                            {excludedTotals.total_uj_apps}
                                            <br />
                                            {excludedTotals.total_uj_apps_percent?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                            {excludedTotals.uj_tracker}
                                            <br />
                                            {excludedTotals.uj_tracker_percent?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-yellow-100">
                                            {excludedTotals.uj_tracker_inc_bursary}
                                            <br />
                                            {excludedTotals.uj_tracker_inc_bursary_percent?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-green-100">
                                            {excludedTotals.from_lease_sent}
                                            <br />
                                            {excludedTotals.percent_from_lease_sent?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-blue-100">
                                            {excludedTotals.from_lease_signed}
                                            <br />
                                            {excludedTotals.percent_from_lease_signed?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-purple-100">
                                            {excludedTotals.from_move_in_booked}
                                            <br />
                                            {excludedTotals.percent_from_move_in_booked?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                          <td className="px-3 py-4 text-sm text-gray-900 bg-orange-100">
                                            {excludedTotals.from_moved_in}
                                            <br />
                                            {excludedTotals.percent_from_moved_in?.toFixed(
                                              2,
                                            )}
                                            %
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            )}

                          {/* Breakdown Section */}
                          {breakdown && (
                            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex justify-between items-center mb-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    {breakdown.location} Breakdown -{" "}
                                    {breakdown.property_name}
                                  </h3>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Total: {breakdown.total_bookings}
                                  </p>
                                </div>
                                <button
                                  onClick={() => setBreakdown(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="h-5 w-5"
                                  />
                                </button>
                              </div>

                              {/* Matrix Table */}
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300 bg-white rounded-lg">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">
                                        Study Year
                                      </th>
                                      {breakdown.funding_types?.map(
                                        (fundingType, idx) => (
                                          <th
                                            key={idx}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-900"
                                          >
                                            {fundingType}
                                          </th>
                                        ),
                                      )}
                                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-900 bg-gray-100">
                                        Total
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {breakdown.study_years?.map(
                                      (studyYear, idx) => (
                                        <tr
                                          key={idx}
                                          className={
                                            idx % 2 === 0
                                              ? "bg-white"
                                              : "bg-gray-50"
                                          }
                                        >
                                          <td className="px-3 py-2 text-sm font-medium text-gray-900 text-left">
                                            {studyYear}
                                          </td>
                                          {breakdown.funding_types?.map(
                                            (fundingType, ftIdx) => {
                                              const cell =
                                                breakdown.matrix?.[studyYear]?.[
                                                  fundingType
                                                ];
                                              return (
                                                <td
                                                  key={ftIdx}
                                                  className="px-3 py-2 text-center text-sm text-gray-700"
                                                >
                                                  {cell && cell.count > 0 ? (
                                                    <div className="p-2">
                                                      <div className="font-semibold text-blue-600">
                                                        {cell.count}
                                                      </div>
                                                      <div className="text-xs text-gray-500">
                                                        {cell.percentage}%
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <span className="text-gray-400">
                                                      -
                                                    </span>
                                                  )}
                                                </td>
                                              );
                                            },
                                          )}
                                          <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                                            <div className="p-2">
                                              <div className="text-blue-700">
                                                {breakdown.study_year_totals?.[
                                                  studyYear
                                                ]?.count || 0}
                                              </div>
                                              <div className="text-xs text-gray-600">
                                                {breakdown.study_year_totals?.[
                                                  studyYear
                                                ]?.percentage || 0}
                                                %
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                    {/* Totals Row */}
                                    <tr className="bg-gray-200 font-bold">
                                      <td className="px-3 py-2 text-sm text-gray-900 text-left">
                                        Total
                                      </td>
                                      {breakdown.funding_types?.map(
                                        (fundingType, idx) => {
                                          const total =
                                            breakdown.funding_type_totals?.[
                                              fundingType
                                            ];
                                          return (
                                            <td
                                              key={idx}
                                              className="px-3 py-2 text-center text-sm text-gray-900"
                                            >
                                              <div className="p-2">
                                                <div className="text-blue-700">
                                                  {total?.count || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                  {total?.percentage || 0}%
                                                </div>
                                              </div>
                                            </td>
                                          );
                                        },
                                      )}
                                      <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                                        <div className="p-2">
                                          <div className="text-blue-700">
                                            {breakdown.total_bookings}
                                          </div>
                                          <div className="text-xs text-gray-600">
                                            100%
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Property Driver Section */}
                          {loadingDriver && (
                            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                              <p className="text-gray-700">
                                Loading property driver...
                              </p>
                            </div>
                          )}

                          {propertyDriver && (
                            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex justify-between items-center mb-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Building Driver -{" "}
                                    {propertyDriver.property_name}
                                  </h3>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {propertyDriver.total_bookings} /{" "}
                                    {propertyDriver.no_beds} beds (
                                    {propertyDriver.overall_percentage}%)
                                  </p>
                                </div>
                                <button
                                  onClick={() => setPropertyDriver(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="h-5 w-5"
                                  />
                                </button>
                              </div>

                              {/* Matrix Table */}
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300 bg-white rounded-lg">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">
                                        Property
                                      </th>
                                      {propertyDriver.funding_types?.map(
                                        (fundingType, idx) => (
                                          <th
                                            key={idx}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-900"
                                          >
                                            {fundingType}
                                          </th>
                                        ),
                                      )}
                                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-900 bg-gray-100">
                                        Total
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {propertyDriver.study_years?.map(
                                      (studyYear, idx) => (
                                        <tr
                                          key={idx}
                                          className={
                                            idx % 2 === 0
                                              ? "bg-white"
                                              : "bg-gray-50"
                                          }
                                        >
                                          <td className="px-3 py-2 text-sm font-medium text-gray-900 text-left">
                                            {studyYear}
                                          </td>
                                          {propertyDriver.funding_types?.map(
                                            (fundingType, ftIdx) => {
                                              const cell =
                                                propertyDriver.matrix?.[
                                                  studyYear
                                                ]?.[fundingType];
                                              return (
                                                <td
                                                  key={ftIdx}
                                                  className="px-3 py-2 text-center text-sm text-gray-700"
                                                >
                                                  {cell && cell.count > 0 ? (
                                                    <button
                                                      onClick={() =>
                                                        handleDriverBulkMessage(
                                                          studyYear,
                                                          fundingType,
                                                        )
                                                      }
                                                      className="hover:bg-blue-50 p-2 rounded transition-colors cursor-pointer w-full"
                                                    >
                                                      <div className="font-semibold text-blue-600">
                                                        {cell.count}
                                                      </div>
                                                      <div className="text-xs text-gray-500">
                                                        {cell.percentage}%
                                                      </div>
                                                    </button>
                                                  ) : (
                                                    <span className="text-gray-400">
                                                      -
                                                    </span>
                                                  )}
                                                </td>
                                              );
                                            },
                                          )}
                                          <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                                            {propertyDriver.study_year_totals?.[
                                              studyYear
                                            ]?.count > 0 ? (
                                              <button
                                                onClick={() =>
                                                  handleDriverBulkMessage(
                                                    studyYear,
                                                    null,
                                                  )
                                                }
                                                className="hover:bg-blue-100 p-2 rounded transition-colors cursor-pointer w-full"
                                              >
                                                <div className="text-blue-700">
                                                  {propertyDriver
                                                    .study_year_totals?.[
                                                    studyYear
                                                  ]?.count || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                  {propertyDriver
                                                    .study_year_totals?.[
                                                    studyYear
                                                  ]?.percentage || 0}
                                                  %
                                                </div>
                                              </button>
                                            ) : (
                                              <div>
                                                <div>
                                                  {propertyDriver
                                                    .study_year_totals?.[
                                                    studyYear
                                                  ]?.count || 0}
                                                </div>
                                                <div className="text-xs text-gray-600">
                                                  {propertyDriver
                                                    .study_year_totals?.[
                                                    studyYear
                                                  ]?.percentage || 0}
                                                  %
                                                </div>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                    {/* Totals Row */}
                                    <tr className="bg-gray-200 font-bold">
                                      <td className="px-3 py-2 text-sm text-gray-900 text-left">
                                        Total
                                      </td>
                                      {propertyDriver.funding_types?.map(
                                        (fundingType, idx) => {
                                          const isNSFASUJ =
                                            fundingType === "NSFAS UJ";
                                          const currentCount =
                                            propertyDriver
                                              .funding_type_totals?.[
                                              fundingType
                                            ]?.count || 0;
                                          const ujTarget =
                                            propertyDriver.uj_students_total ||
                                            0;
                                          const difference =
                                            isNSFASUJ && ujTarget > 0
                                              ? currentCount - ujTarget
                                              : null;
                                          const isOver = difference > 0;
                                          const isUnder = difference < 0;

                                          return (
                                            <td
                                              key={idx}
                                              className="px-3 py-2 text-center text-sm text-gray-900"
                                            >
                                              {currentCount > 0 ? (
                                                <button
                                                  onClick={() =>
                                                    handleDriverBulkMessage(
                                                      null,
                                                      fundingType,
                                                    )
                                                  }
                                                  className="hover:bg-blue-100 p-2 rounded transition-colors cursor-pointer w-full"
                                                >
                                                  <div className="text-blue-700">
                                                    {currentCount}
                                                  </div>
                                                  <div className="text-xs text-gray-700">
                                                    {propertyDriver
                                                      .funding_type_totals?.[
                                                      fundingType
                                                    ]?.percentage || 0}
                                                    %
                                                  </div>
                                                  {isNSFASUJ &&
                                                    ujTarget > 0 && (
                                                      <div className="text-xs mt-1">
                                                        <div className="text-gray-700">
                                                          Allowed: {ujTarget}
                                                        </div>
                                                        <div
                                                          className={`font-semibold ${isOver ? "text-red-600" : isUnder ? "text-green-600" : "text-gray-700"}`}
                                                        >
                                                          {isOver
                                                            ? `Over: +${difference}`
                                                            : isUnder
                                                              ? `Under: ${difference}`
                                                              : "On Target"}
                                                        </div>
                                                      </div>
                                                    )}
                                                </button>
                                              ) : (
                                                <div>
                                                  <div>{currentCount}</div>
                                                  <div className="text-xs text-gray-700">
                                                    {propertyDriver
                                                      .funding_type_totals?.[
                                                      fundingType
                                                    ]?.percentage || 0}
                                                    %
                                                  </div>
                                                  {isNSFASUJ &&
                                                    ujTarget > 0 && (
                                                      <div className="text-xs mt-1">
                                                        <div className="text-gray-700">
                                                          Allowed: {ujTarget}
                                                        </div>
                                                        <div className="font-semibold text-red-600">
                                                          Under: {difference}
                                                        </div>
                                                      </div>
                                                    )}
                                                </div>
                                              )}
                                            </td>
                                          );
                                        },
                                      )}
                                      <td className="px-3 py-2 text-center text-sm text-gray-900 bg-gray-300">
                                        {propertyDriver.total_bookings > 0 ? (
                                          <button
                                            onClick={() =>
                                              handleDriverBulkMessage(
                                                null,
                                                null,
                                              )
                                            }
                                            className="hover:bg-blue-200 p-2 rounded transition-colors cursor-pointer w-full"
                                          >
                                            <div className="text-blue-800">
                                              {propertyDriver.total_bookings}
                                            </div>
                                            <div className="text-xs">
                                              {
                                                propertyDriver.overall_percentage
                                              }
                                              %
                                            </div>
                                          </button>
                                        ) : (
                                          <div>
                                            <div>
                                              {propertyDriver.total_bookings}
                                            </div>
                                            <div className="text-xs">
                                              {
                                                propertyDriver.overall_percentage
                                              }
                                              %
                                            </div>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {/* Folder Breakdown Section */}
                          {loadingFolderBreakdown && (
                            <div
                              ref={folderBreakdownRef}
                              className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg text-center"
                            >
                              <p className="text-gray-700">
                                Loading folder breakdown...
                              </p>
                            </div>
                          )}

                          {folderBreakdown && (
                            <div
                              ref={folderBreakdownRef}
                              className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg"
                            >
                              <div className="flex justify-between items-center mb-4">
                                <div>
                                  <h3 className="text-lg font-semibold text-gray-900">
                                    Funding Type Breakdown -{" "}
                                    {folderBreakdown.location}
                                  </h3>
                                  <p className="text-sm text-gray-600 mt-1">
                                    Total bookings:{" "}
                                    {folderBreakdown.total_bookings}
                                  </p>
                                </div>
                                <button
                                  onClick={() => setFolderBreakdown(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="h-5 w-5"
                                  />
                                </button>
                              </div>

                              {/* Matrix Table */}
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300 bg-white rounded-lg">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">
                                        Study Year
                                      </th>
                                      {folderBreakdown.funding_types?.map(
                                        (fundingType, idx) => (
                                          <th
                                            key={idx}
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-900"
                                          >
                                            {fundingType}
                                          </th>
                                        ),
                                      )}
                                      <th className="px-3 py-2 text-center text-xs font-semibold text-gray-900 bg-gray-100">
                                        Total
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {folderBreakdown.study_years?.map(
                                      (studyYear, idx) => (
                                        <tr
                                          key={idx}
                                          className={
                                            idx % 2 === 0
                                              ? "bg-white"
                                              : "bg-gray-50"
                                          }
                                        >
                                          <td className="px-3 py-2 text-sm font-medium text-gray-900 text-left">
                                            {studyYear}
                                          </td>
                                          {folderBreakdown.funding_types?.map(
                                            (fundingType, ftIdx) => {
                                              const cell =
                                                folderBreakdown.matrix?.[
                                                  studyYear
                                                ]?.[fundingType];
                                              return (
                                                <td
                                                  key={ftIdx}
                                                  className="px-3 py-2 text-center text-sm text-gray-700"
                                                >
                                                  {cell && cell.count > 0 ? (
                                                    <div className="p-2">
                                                      <div className="font-semibold text-blue-600">
                                                        {cell.count}
                                                      </div>
                                                      <div className="text-xs text-gray-500">
                                                        {cell.percentage}%
                                                      </div>
                                                    </div>
                                                  ) : (
                                                    <span className="text-gray-400">
                                                      -
                                                    </span>
                                                  )}
                                                </td>
                                              );
                                            },
                                          )}
                                          <td className="px-3 py-2 text-center text-sm font-bold text-gray-900 bg-gray-100">
                                            <div className="p-2">
                                              <div className="text-blue-700">
                                                {folderBreakdown
                                                  .study_year_totals?.[
                                                  studyYear
                                                ]?.count || 0}
                                              </div>
                                              <div className="text-xs text-gray-600">
                                                {folderBreakdown
                                                  .study_year_totals?.[
                                                  studyYear
                                                ]?.percentage || 0}
                                                %
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                    {/* Totals Row */}
                                    <tr className="bg-gray-200 font-bold">
                                      <td className="px-3 py-2 text-sm text-gray-900 text-left">
                                        Total
                                      </td>
                                      {folderBreakdown.funding_types?.map(
                                        (fundingType, idx) => {
                                          const total =
                                            folderBreakdown
                                              .funding_type_totals?.[
                                              fundingType
                                            ];
                                          return (
                                            <td
                                              key={idx}
                                              className="px-3 py-2 text-center text-sm text-gray-900"
                                            >
                                              <div className="p-2">
                                                <div className="text-blue-700">
                                                  {total?.count || 0}
                                                </div>
                                                <div className="text-xs text-gray-700">
                                                  {total?.percentage || 0}%
                                                </div>
                                              </div>
                                            </td>
                                          );
                                        },
                                      )}
                                      <td className="px-3 py-2 text-center text-sm text-gray-900 bg-gray-300">
                                        <div className="p-2">
                                          <div className="text-blue-800">
                                            {folderBreakdown.total_bookings}
                                          </div>
                                          <div className="text-xs">100%</div>
                                        </div>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
